import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  RectangleStackIcon,
  PencilIcon,
  ChartBarIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// ─── Provenance ──────────────────────────────────────────────────────────────

type Provenance = 'saisi' | 'moyenne' | 'calcule';

const PROVENANCE_META: Record<Provenance, { label: string; dotClass: string }> = {
  saisi:   { label: 'Saisi — valeur que vous avez fixée · clic droit pour explorer les projets',      dotClass: 'bg-violet-500' },
  moyenne: { label: 'Médiane historique des projets · clic droit pour ajuster la sélection',            dotClass: 'bg-gray-400' },
  calcule: { label: 'Calculé — dérivé d\'une saisie ou d\'un ratio', dotClass: 'bg-green-500' },
};

const ProvenanceDot: React.FC<{ type: Provenance; className?: string }> = ({ type, className }) => (
  <span
    className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${PROVENANCE_META[type].dotClass} ${className ?? ''}`}
    title={PROVENANCE_META[type].label}
  />
);

// ─── References (benchmarks) ─────────────────────────────────────────────────

type ProjectSample = { nom: string; valeur: number };

type RefSource = {
  nbProjets: number;
  contextLabel: string;
  range: { min: number; max: number };
  projets: ProjectSample[];
};

export type RatioRef = { value: number; unit: string; source: RefSource };
type PuRef = { value: number; unit: string; source: RefSource };

// ─── Mock generator ──────────────────────────────────────────────────────────

const PROJETS_RESIDNEUF = [
  'Clichy · 42 logts', 'Meudon · 68 logts', 'Issy · 95 logts', 'Gennevilliers · 110',
  'Saint-Denis · 75 logts', 'Nanterre · 48 logts', 'Vitry · 88 logts', 'Montreuil · 120 logts',
  'Bagneux · 36 logts', 'Pantin · 92 logts', 'Asnières · 60 logts', 'Boulogne · 52 logts',
];
const PROJETS_TERTIAIRE = [
  'La Défense T4', 'Bercy Village', 'Paris RdP', 'Issy Tour',
  'Neuilly Axe', 'Saint-Ouen Pulse', 'Montrouge HQ', 'Genn. Logistique',
];

function mkSource(nb: number, ctx: string, center: number, spread: number, names: string[]): RefSource {
  const projets: ProjectSample[] = Array.from({ length: nb }, (_, i) => ({
    nom: names[i % names.length],
    valeur: +(center + Math.sin(i * 1.7) * spread * 0.9).toFixed(2),
  }));
  return {
    nbProjets: nb,
    contextLabel: ctx,
    range: { min: +(center - spread).toFixed(2), max: +(center + spread).toFixed(2) },
    projets,
  };
}

// ─── Templates ───────────────────────────────────────────────────────────────

export type TypologieKey = 'resid-neuf' | 'tertiaire-neuf';

export type Template = {
  label: string;
  moProductionRatio: RatioRef;
  moIndirecteRatio: RatioRef;
  acierRatio: RatioRef;
  betonPu: PuRef;
  acierPu: PuRef;
  prefaPu: PuRef;
  encadrementPu: PuRef;
  bennesGoPu: PuRef;
  bennesCesPu: PuRef;
  baseViePu: PuRef;
  instaElecPu: PuRef;
  trFourgonsPu: PuRef;
  trMaterielsPu: PuRef;
  consoPu: PuRef;
  fraisDiversPu: PuRef;
  coffragePu: PuRef;
};

const CTX_RESID = 'Résidentiel · Neuf · 2022-2025';
const CTX_TERT = 'Tertiaire · Neuf · 2022-2025';

export const TEMPLATES: Record<TypologieKey, Template> = {
  'resid-neuf': {
    label: 'Résidentiel · Neuf',
    moProductionRatio: { value: 8.11,  unit: 'H/m³',     source: mkSource(12, CTX_RESID, 8.11,  1.2,   PROJETS_RESIDNEUF) },
    moIndirecteRatio:  { value: 3.20,  unit: 'H/m³',     source: mkSource(12, CTX_RESID, 3.20,  0.6,   PROJETS_RESIDNEUF) },
    acierRatio:        { value: 63.80, unit: 'kg/m³',    source: mkSource(12, CTX_RESID, 63.80, 8,     PROJETS_RESIDNEUF) },
    betonPu:           { value: 164.63, unit: '€/m³',    source: mkSource(12, CTX_RESID, 164.63, 20,    PROJETS_RESIDNEUF) },
    acierPu:           { value: 0.94,  unit: '€/kg',     source: mkSource(12, CTX_RESID, 0.94,  0.12,  PROJETS_RESIDNEUF) },
    prefaPu:           { value: 19.49, unit: '€/m²',     source: mkSource(12, CTX_RESID, 19.49, 3,     PROJETS_RESIDNEUF) },
    encadrementPu:     { value: 34258.87, unit: '€/mois', source: mkSource(12, CTX_RESID, 34258, 3500, PROJETS_RESIDNEUF) },
    bennesGoPu:        { value: 820,   unit: '€/mois',   source: mkSource(12, CTX_RESID, 820,   180,   PROJETS_RESIDNEUF) },
    bennesCesPu:       { value: 620,   unit: '€/mois',   source: mkSource(12, CTX_RESID, 620,   140,   PROJETS_RESIDNEUF) },
    baseViePu:         { value: 2100,  unit: '€/mois',   source: mkSource(12, CTX_RESID, 2100,  320,   PROJETS_RESIDNEUF) },
    instaElecPu:       { value: 42.06, unit: '€',        source: mkSource(12, CTX_RESID, 42.06, 8,     PROJETS_RESIDNEUF) },
    trFourgonsPu:      { value: 3869.41, unit: '€/mois', source: mkSource(12, CTX_RESID, 3869,  450,   PROJETS_RESIDNEUF) },
    trMaterielsPu:     { value: 3260,  unit: '€/mois',   source: mkSource(12, CTX_RESID, 3260,  380,   PROJETS_RESIDNEUF) },
    consoPu:           { value: 6030.80, unit: '€/mois', source: mkSource(12, CTX_RESID, 6030,  720,   PROJETS_RESIDNEUF) },
    fraisDiversPu:     { value: 7.72,  unit: '€/m²',     source: mkSource(12, CTX_RESID, 7.72,  1.5,   PROJETS_RESIDNEUF) },
    coffragePu:        { value: 81.58, unit: '€/m²',     source: mkSource(12, CTX_RESID, 81.58, 9,     PROJETS_RESIDNEUF) },
  },
  'tertiaire-neuf': {
    label: 'Tertiaire · Neuf',
    moProductionRatio: { value: 6.20,  unit: 'H/m³',     source: mkSource(8, CTX_TERT, 6.20,  0.9,   PROJETS_TERTIAIRE) },
    moIndirecteRatio:  { value: 2.40,  unit: 'H/m³',     source: mkSource(8, CTX_TERT, 2.40,  0.4,   PROJETS_TERTIAIRE) },
    acierRatio:        { value: 85,    unit: 'kg/m³',    source: mkSource(8, CTX_TERT, 85,    12,    PROJETS_TERTIAIRE) },
    betonPu:           { value: 172,   unit: '€/m³',     source: mkSource(8, CTX_TERT, 172,   18,    PROJETS_TERTIAIRE) },
    acierPu:           { value: 0.98,  unit: '€/kg',     source: mkSource(8, CTX_TERT, 0.98,  0.10,  PROJETS_TERTIAIRE) },
    prefaPu:           { value: 24.50, unit: '€/m²',     source: mkSource(8, CTX_TERT, 24.50, 3,     PROJETS_TERTIAIRE) },
    encadrementPu:     { value: 38500, unit: '€/mois',   source: mkSource(8, CTX_TERT, 38500, 4200,  PROJETS_TERTIAIRE) },
    bennesGoPu:        { value: 1050,  unit: '€/mois',   source: mkSource(8, CTX_TERT, 1050,  200,   PROJETS_TERTIAIRE) },
    bennesCesPu:       { value: 750,   unit: '€/mois',   source: mkSource(8, CTX_TERT, 750,   160,   PROJETS_TERTIAIRE) },
    baseViePu:         { value: 2600,  unit: '€/mois',   source: mkSource(8, CTX_TERT, 2600,  380,   PROJETS_TERTIAIRE) },
    instaElecPu:       { value: 58,    unit: '€',        source: mkSource(8, CTX_TERT, 58,    10,    PROJETS_TERTIAIRE) },
    trFourgonsPu:      { value: 4200,  unit: '€/mois',   source: mkSource(8, CTX_TERT, 4200,  500,   PROJETS_TERTIAIRE) },
    trMaterielsPu:     { value: 3650,  unit: '€/mois',   source: mkSource(8, CTX_TERT, 3650,  420,   PROJETS_TERTIAIRE) },
    consoPu:           { value: 7200,  unit: '€/mois',   source: mkSource(8, CTX_TERT, 7200,  850,   PROJETS_TERTIAIRE) },
    fraisDiversPu:     { value: 9.50,  unit: '€/m²',     source: mkSource(8, CTX_TERT, 9.50,  1.8,   PROJETS_TERTIAIRE) },
    coffragePu:        { value: 94,    unit: '€/m²',     source: mkSource(8, CTX_TERT, 94,    10,    PROJETS_TERTIAIRE) },
  },
};

// ─── Line defs ───────────────────────────────────────────────────────────────

type LineDriver =
  | { kind: 'mo';        ratio: RatioRef }
  | { kind: 'acier';     ratio: RatioRef; pu: PuRef }
  | { kind: 'from-m3';   pu: PuRef }
  | { kind: 'from-shot'; pu: PuRef }
  | { kind: 'from-duree'; pu: PuRef }
  | { kind: 'fixe';      pu: PuRef };

type LineDef = {
  id: string;
  label: string;
  category: 'mo' | 'materiaux' | 'fc';
  unitCell: string;
  qtyUnit: string;
  driver: LineDriver;
};

export const buildLines = (t: Template): LineDef[] => [
  { id: 'mo-prod',      label: "Main d'œuvre Production", category: 'mo',         unitCell: 'H/M3',     qtyUnit: 'H',    driver: { kind: 'mo',        ratio: t.moProductionRatio } },
  { id: 'mo-ind',       label: "Main d'œuvre indirecte",  category: 'mo',         unitCell: 'H/M3',     qtyUnit: 'H',    driver: { kind: 'mo',        ratio: t.moIndirecteRatio } },
  { id: 'betons',       label: "Bétons",                   category: 'materiaux',  unitCell: 'm3 béton', qtyUnit: 'm³',   driver: { kind: 'from-m3',   pu: t.betonPu } },
  { id: 'acier',        label: "Acier",                    category: 'materiaux',  unitCell: 'kg',       qtyUnit: 'kg',   driver: { kind: 'acier',     ratio: t.acierRatio, pu: t.acierPu } },
  { id: 'prefa',        label: "Préfa + matériaux",        category: 'materiaux',  unitCell: 'm² Shot',  qtyUnit: 'm²',   driver: { kind: 'from-shot', pu: t.prefaPu } },
  { id: 'encadr',       label: "Encadrement",              category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.encadrementPu } },
  { id: 'bennes-go',    label: "Bennes GO",                category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.bennesGoPu } },
  { id: 'bennes-ces',   label: "Bennes CES",               category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.bennesCesPu } },
  { id: 'base-vie',     label: "Base vie",                 category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.baseViePu } },
  { id: 'insta-elec',   label: "Installation électrique et branchements", category: 'fc', unitCell: 'Fixe', qtyUnit: '-', driver: { kind: 'fixe',       pu: t.instaElecPu } },
  { id: 'tr-fourgons',  label: "Transports Fourgons",      category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.trFourgonsPu } },
  { id: 'tr-materiels', label: "Transports matériels",     category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.trMaterielsPu } },
  { id: 'conso',        label: "Consommations",            category: 'fc',         unitCell: 'Mois',     qtyUnit: 'mois', driver: { kind: 'from-duree', pu: t.consoPu } },
  { id: 'frais-divers', label: "Frais divers",             category: 'fc',         unitCell: 'm² Shot',  qtyUnit: 'm²',   driver: { kind: 'from-shot', pu: t.fraisDiversPu } },
  { id: 'coffrage',     label: "Coffrage",                 category: 'fc',         unitCell: 'm² Shot',  qtyUnit: 'm²',   driver: { kind: 'from-shot', pu: t.coffragePu } },
];

// ─── Grid layout ─────────────────────────────────────────────────────────────

const COL_CLASS = "grid grid-cols-[minmax(180px,1fr)_90px_120px_150px_130px_140px] gap-x-3 items-center";

// ─── Editable cell ───────────────────────────────────────────────────────────

interface EditableCellProps {
  value: number;
  provenance: Provenance;
  onChange: (v: number) => void;
  onReset?: () => void;
  decimals?: number;
  onContextMenu?: (e: React.MouseEvent) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value, provenance, onChange, onReset, decimals = 2, onContextMenu,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed !== value) onChange(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        step="any"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); }
        }}
        className="w-full px-1 py-0 border border-violet-400 rounded text-sm font-semibold text-violet-700 text-right focus:outline-none focus:ring-1 focus:ring-violet-500 tabular-nums"
      />
    );
  }

  const isOverridden = provenance === 'saisi';
  const color = isOverridden ? 'text-violet-700' : 'text-gray-900';
  const hasSourcePicker = !!onContextMenu;

  return (
    <div
      className="flex items-center justify-end gap-1"
      onContextMenu={onContextMenu}
    >
      {isOverridden && onReset && (
        <button onClick={onReset} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Réinitialiser à la moyenne">
          <ArrowUturnLeftIcon className="w-3 h-3" />
        </button>
      )}
      <ProvenanceDot type={provenance} />
      <button
        onClick={() => setEditing(true)}
        className={`text-sm font-semibold ${color} hover:text-violet-700 tabular-nums ${hasSourcePicker ? 'cursor-context-menu' : ''}`}
        title={hasSourcePicker ? 'Clic pour modifier · Clic droit pour choisir les projets source' : 'Cliquer pour modifier'}
      >
        {value.toLocaleString('fr-FR', { maximumFractionDigits: decimals })}
      </button>
    </div>
  );
};

// ─── Source line (inside expansion panel) ────────────────────────────────────

const SourceLine: React.FC<{ label: string; refData: RatioRef | PuRef }> = ({ label, refData }) => {
  const [listOpen, setListOpen] = useState(false);
  return (
    <div className="text-[11px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-gray-500">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="mx-1 text-gray-300">·</span>
          médiane <span className="font-mono text-gray-700">{refData.value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</span> {refData.unit}
          <span className="mx-1 text-gray-300">·</span>
          intervalle {refData.source.range.min.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}–{refData.source.range.max.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
          <span className="mx-1 text-gray-300">·</span>
          <span className="italic">{refData.source.contextLabel}</span>
        </span>
        <button
          onClick={() => setListOpen(!listOpen)}
          className="inline-flex items-center gap-0.5 text-violet-600 hover:text-violet-800 whitespace-nowrap"
        >
          {listOpen ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
          {listOpen ? 'Masquer' : `Voir ${refData.source.nbProjets} projets`}
        </button>
      </div>
      {listOpen && (
        <div className="mt-1 bg-white border border-gray-200 rounded p-2 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5">
          {refData.source.projets.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className="text-gray-600 truncate">{p.nom}</span>
              <span className="font-mono text-gray-500 flex-shrink-0 text-[10px]">
                {p.valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Saisie input (header) ───────────────────────────────────────────────────

export const SaisieInput: React.FC<{
  label: string;
  unit: string;
  value: number;
  onChange: (v: number) => void;
  decimals?: number;
  /** Valeur dérivée d'une médiane, pas saisie : affichée en gris avec « ≈ » */
  estimated?: boolean;
}> = ({ label, unit, value, onChange, decimals = 2, estimated }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed !== value) onChange(parsed);
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em]">{label}</span>
      {editing ? (
        <input
          autoFocus
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); }
          }}
          className="w-32 px-1.5 py-0.5 border border-violet-400 rounded text-base font-semibold text-violet-700 focus:outline-none focus:ring-1 focus:ring-violet-500 tabular-nums"
        />
      ) : (
        <button onClick={() => setEditing(true)} className="inline-flex items-baseline gap-1 text-left group">
          <ProvenanceDot type={estimated ? 'moyenne' : 'saisi'} className="self-center" />
          <span className={`text-base font-semibold ${estimated ? 'text-gray-400' : 'text-gray-900'} group-hover:text-violet-700 tabular-nums`}>
            {estimated && '≈ '}{value.toLocaleString('fr-FR', { maximumFractionDigits: decimals })}
          </span>
          <span className="text-xs text-gray-400">{unit}</span>
        </button>
      )}
    </div>
  );
};

// ─── Line computation ────────────────────────────────────────────────────────

export interface Saisies {
  shot: number;
  m3: number;
  duree: number;
  tauxHoraire: number;
}

interface LineComputation {
  ratio: { value: number; provenance: Provenance; ref: RatioRef } | null;
  qty: { value: number; unit: string };
  pu: { value: number; provenance: Provenance; ref: PuRef | null };
  total: number;
}

export function computeLine(
  line: LineDef,
  override: { ratio?: number | null; pu?: number | null } | undefined,
  saisies: Saisies,
): LineComputation {
  const ratioOv = override?.ratio;
  const puOv = override?.pu;

  switch (line.driver.kind) {
    case 'mo': {
      const rVal = ratioOv ?? line.driver.ratio.value;
      const rProv: Provenance = ratioOv != null ? 'saisi' : 'moyenne';
      const qty = rVal * saisies.m3;
      return {
        ratio: { value: rVal, provenance: rProv, ref: line.driver.ratio },
        qty: { value: qty, unit: line.qtyUnit },
        pu: { value: saisies.tauxHoraire, provenance: 'calcule', ref: null },
        total: qty * saisies.tauxHoraire,
      };
    }
    case 'acier': {
      const rVal = ratioOv ?? line.driver.ratio.value;
      const rProv: Provenance = ratioOv != null ? 'saisi' : 'moyenne';
      const qty = rVal * saisies.m3;
      const pVal = puOv ?? line.driver.pu.value;
      const pProv: Provenance = puOv != null ? 'saisi' : 'moyenne';
      return {
        ratio: { value: rVal, provenance: rProv, ref: line.driver.ratio },
        qty: { value: qty, unit: line.qtyUnit },
        pu: { value: pVal, provenance: pProv, ref: line.driver.pu },
        total: qty * pVal,
      };
    }
    case 'from-m3':
    case 'from-shot':
    case 'from-duree':
    case 'fixe': {
      const qty =
        line.driver.kind === 'from-m3'   ? saisies.m3    :
        line.driver.kind === 'from-shot' ? saisies.shot  :
        line.driver.kind === 'from-duree' ? saisies.duree :
        1;
      const pVal = puOv ?? line.driver.pu.value;
      const pProv: Provenance = puOv != null ? 'saisi' : 'moyenne';
      return {
        ratio: null,
        qty: { value: qty, unit: line.qtyUnit },
        pu: { value: pVal, provenance: pProv, ref: line.driver.pu },
        total: qty * pVal,
      };
    }
  }
}

// ─── Source picker modal (mirrors 3-chiffrage/PuSourcesModal) ────────────────

interface SourcePickerModalProps {
  title: string;
  subtitle: string;
  unit: string;
  source: RefSource;
  currentValue: number;
  onApply: (v: number) => void;
  onClose: () => void;
}

const SourcePickerModal: React.FC<SourcePickerModalProps> = ({
  title, subtitle, unit, source, currentValue, onApply, onClose,
}) => {
  const [actives, setActives] = useState<boolean[]>(() => source.projets.map(() => true));

  const activeValues = useMemo(
    () => source.projets.filter((_, i) => actives[i]).map(p => p.valeur),
    [source.projets, actives],
  );

  const stats = useMemo(() => {
    if (activeValues.length === 0) return null;
    const sorted = [...activeValues].sort((a, b) => a - b);
    const sum = sorted.reduce((a, b) => a + b, 0);
    const mid = Math.floor(sorted.length / 2);
    return {
      min: sorted[0],
      max: sorted[sorted.length - 1],
      avg: sum / sorted.length,
      median: sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid],
      count: sorted.length,
    };
  }, [activeValues]);

  const toggle = (i: number) =>
    setActives(prev => prev.map((a, idx) => (idx === i ? !a : a)));

  const handleApply = () => {
    if (!stats) return;
    onApply(stats.avg);
    onClose();
  };

  const fmt = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[680px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-sm">{subtitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-3 bg-violet-50 border-b border-violet-100 flex items-center gap-8">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-0.5">Sélectionnés</div>
            <div className="text-sm font-semibold text-violet-700">
              {activeValues.length} / {source.projets.length}
            </div>
          </div>
          {stats ? (
            <>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Min</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.min)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Max</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.max)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-0.5">Médiane</div>
                <div className="text-sm font-medium text-gray-700">{fmt(stats.median)}</div>
              </div>
              <div className="flex-1 text-right">
                <div className="text-xs text-gray-500 mb-0.5">Nouvelle valeur (moyenne)</div>
                <div className="text-lg font-bold text-violet-700">
                  {fmt(stats.avg)} <span className="text-xs font-medium text-violet-500">{unit}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 text-center text-sm text-gray-400 italic">
              Sélectionnez au moins un projet
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
              <tr>
                <th className="w-10 px-4 py-2" />
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Chantier</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Valeur</th>
              </tr>
            </thead>
            <tbody>
              {source.projets.map((p, i) => {
                const active = actives[i];
                return (
                  <tr
                    key={i}
                    onClick={() => toggle(i)}
                    className={`cursor-pointer border-b border-gray-50 transition-colors ${
                      active
                        ? 'bg-white hover:bg-violet-50/40'
                        : 'bg-gray-50/60 hover:bg-gray-100/60'
                    }`}
                  >
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggle(i)}
                        onClick={e => e.stopPropagation()}
                        className="w-4 h-4 text-violet-600 rounded border-gray-300 focus:ring-violet-500 cursor-pointer"
                      />
                    </td>
                    <td className={`px-4 py-2.5 font-medium ${active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                      {p.nom}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${active ? 'text-violet-600' : 'text-gray-300'}`}>
                      {fmt(p.valeur)} <span className="text-[10px] font-normal text-gray-400">{unit}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {source.projets.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">
              Aucune source disponible
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Décochez les projets aberrants pour affiner la moyenne · Valeur actuelle <span className="font-mono text-gray-600">{fmt(currentValue)} {unit}</span>
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleApply}
              disabled={!stats || stats.count === 0}
              className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Appliquer — {stats ? fmt(stats.avg) : '—'} {unit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Line row ────────────────────────────────────────────────────────────────

interface LineRowProps {
  line: LineDef;
  comp: LineComputation;
  expanded: boolean;
  onToggleExpand: () => void;
  onRatioChange: (v: number) => void;
  onRatioReset: () => void;
  onPuChange: (v: number) => void;
  onPuReset: () => void;
  onOpenRatioModal?: () => void;
  onOpenPuModal?: () => void;
}

const LineRow: React.FC<LineRowProps> = ({
  line, comp, expanded, onToggleExpand,
  onRatioChange, onRatioReset, onPuChange, onPuReset,
  onOpenRatioModal, onOpenPuModal,
}) => {
  const hasSource = comp.ratio?.ref || comp.pu.ref;
  const isMo = line.driver.kind === 'mo';

  return (
    <>
      <div className={`${COL_CLASS} py-1 hover:bg-gray-50 -mx-2 px-2 rounded`}>
        {/* Libellé */}
        <div className="flex items-center min-w-0">
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-1 text-sm text-gray-800 hover:text-violet-700 text-left min-w-0"
            title="Voir les sources projets"
          >
            {expanded
              ? <ChevronDownIcon  className="w-3 h-3 text-gray-400 flex-shrink-0" />
              : <ChevronRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            <span className="truncate">{line.label}</span>
          </button>
        </div>

        {/* Unité */}
        <span className="text-xs text-gray-500">{line.unitCell}</span>

        {/* Ratio */}
        <div>
          {comp.ratio ? (
            <EditableCell
              value={comp.ratio.value}
              provenance={comp.ratio.provenance}
              onChange={onRatioChange}
              onReset={comp.ratio.provenance === 'saisi' ? onRatioReset : undefined}
              decimals={2}
              onContextMenu={onOpenRatioModal ? (e) => { e.preventDefault(); onOpenRatioModal(); } : undefined}
            />
          ) : (
            <span className="block text-right text-gray-300 text-sm">—</span>
          )}
        </div>

        {/* Qty */}
        <div className="flex items-center justify-end gap-1">
          <span className="text-sm text-gray-700 tabular-nums">
            {comp.qty.value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
          </span>
          <span className="text-[10px] text-gray-400 w-7 text-left">{comp.qty.unit}</span>
        </div>

        {/* PU */}
        <div>
          {isMo ? (
            <div className="flex items-center justify-end gap-1" title="Calculé — hérité du taux horaire saisi en en-tête">
              <ProvenanceDot type="calcule" />
              <span className="text-sm font-semibold text-gray-700 tabular-nums">
                {comp.pu.value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
              </span>
            </div>
          ) : (
            <EditableCell
              value={comp.pu.value}
              provenance={comp.pu.provenance}
              onChange={onPuChange}
              onReset={comp.pu.provenance === 'saisi' ? onPuReset : undefined}
              decimals={2}
              onContextMenu={onOpenPuModal ? (e) => { e.preventDefault(); onOpenPuModal(); } : undefined}
            />
          )}
        </div>

        {/* Total */}
        <div className="text-right text-sm font-semibold text-gray-900 tabular-nums">
          {comp.total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
        </div>
      </div>

      {/* Source disclosure */}
      {expanded && hasSource && (
        <div className="my-1 bg-gray-50 border-l-2 border-violet-200 ml-5 mr-2 px-3 py-2 rounded-r space-y-1.5">
          {comp.ratio?.ref && <SourceLine label="Ratio" refData={comp.ratio.ref} />}
          {comp.pu.ref && <SourceLine label="PU" refData={comp.pu.ref} />}
        </div>
      )}
    </>
  );
};

// ─── Overrides type ──────────────────────────────────────────────────────────

type LineOverrides = Record<string, { ratio?: number | null; pu?: number | null }>;

// ─── Modal state ─────────────────────────────────────────────────────────────

type ModalState = {
  title: string;
  subtitle: string;
  unit: string;
  source: RefSource;
  currentValue: number;
  onApply: (v: number) => void;
};

// ─── Main component ──────────────────────────────────────────────────────────

const RatioGrosOeuvrePrototype: React.FC = () => {
  const [typologie, setTypologie] = useState<TypologieKey>('resid-neuf');
  const [saisies, setSaisies] = useState<Saisies>({ shot: 1260, m3: 750, duree: 5, tauxHoraire: 43 });
  const [overrides, setOverrides] = useState<LineOverrides>({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState | null>(null);

  const template = TEMPLATES[typologie];
  const lines = buildLines(template);

  const computations = lines.map(line => ({
    line,
    comp: computeLine(line, overrides[line.id], saisies),
  }));

  const moLines = computations.filter(c => c.line.category === 'mo');
  const matLines = computations.filter(c => c.line.category === 'materiaux');
  const fcLines = computations.filter(c => c.line.category === 'fc');

  const totalMo = moLines.reduce((s, c) => s + c.comp.total, 0);
  const totalMat = matLines.reduce((s, c) => s + c.comp.total, 0);
  const totalDbs = totalMo + totalMat;
  const totalFc = fcLines.reduce((s, c) => s + c.comp.total, 0);
  const grandTotal = totalDbs + totalFc;

  const switchTypologie = (key: TypologieKey) => {
    setTypologie(key);
    setOverrides({});
  };

  const setLineRatio = (id: string, v: number | null) => {
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], ratio: v } }));
  };
  const setLinePu = (id: string, v: number | null) => {
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], pu: v } }));
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const nbOverrides = Object.values(overrides).filter(o => o.ratio != null || o.pu != null).length;

  // Modal openers
  const openRatioModal = (line: LineDef, currentValue: number) => {
    if (line.driver.kind !== 'mo' && line.driver.kind !== 'acier') return;
    const ref = line.driver.ratio;
    setModal({
      title: `Ratio — ${line.label}`,
      subtitle: `Médiane des ${ref.source.nbProjets} projets · ${ref.source.contextLabel}`,
      unit: ref.unit,
      source: ref.source,
      currentValue,
      onApply: (v) => setLineRatio(line.id, v),
    });
  };

  const openPuModal = (line: LineDef, currentValue: number) => {
    if (line.driver.kind === 'mo') return;
    const ref = line.driver.pu;
    setModal({
      title: `Prix unitaire — ${line.label}`,
      subtitle: `Médiane des ${ref.source.nbProjets} projets · ${ref.source.contextLabel}`,
      unit: ref.unit,
      source: ref.source,
      currentValue,
      onApply: (v) => setLinePu(line.id, v),
    });
  };

  const renderLine = ({ line, comp }: { line: LineDef; comp: LineComputation }) => (
    <LineRow
      key={line.id}
      line={line}
      comp={comp}
      expanded={expandedIds.has(line.id)}
      onToggleExpand={() => toggleExpand(line.id)}
      onRatioChange={(v) => setLineRatio(line.id, v)}
      onRatioReset={() => setLineRatio(line.id, null)}
      onPuChange={(v) => setLinePu(line.id, v)}
      onPuReset={() => setLinePu(line.id, null)}
      onOpenRatioModal={comp.ratio ? () => openRatioModal(line, comp.ratio!.value) : undefined}
      onOpenPuModal={comp.pu.ref ? () => openPuModal(line, comp.pu.value) : undefined}
    />
  );

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 italic px-1">
        Prototype lot <strong className="text-gray-700 not-italic">Gros Œuvre</strong> ·
        {' '}4 saisies <strong className="text-gray-700 not-italic">(SHOT, M³ béton, durée, taux horaire)</strong>
        {' '}propagées via ratios et PUs historiques aux 15 lignes — main d'œuvre, matériaux, frais de chantier.
        {' '}<span className="text-gray-400">Clic gauche pour éditer · clic droit sur une valeur pour choisir les projets source.</span>
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <RectangleStackIcon className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Gros Œuvre</span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-600">{template.label}</span>
            {nbOverrides > 0 && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-[11px] text-violet-600 inline-flex items-center gap-1">
                  <PencilIcon className="w-3 h-3" />
                  {nbOverrides} override{nbOverrides > 1 ? 's' : ''}
                  <button
                    onClick={() => setOverrides({})}
                    className="ml-1 text-gray-400 hover:text-gray-700 underline"
                  >
                    tout réinitialiser
                  </button>
                </span>
              </>
            )}
          </div>
          <div className="flex gap-1">
            {(Object.entries(TEMPLATES) as [TypologieKey, Template][]).map(([key, t]) => (
              <button
                key={key}
                onClick={() => switchTypologie(key)}
                className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                  typologie === key
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-400 hover:text-violet-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Saisies */}
        <div className="flex items-start gap-8 py-4 border-b border-gray-100">
          <SaisieInput label="SHOT"         unit="m² shot" value={saisies.shot}        onChange={(v) => setSaisies(s => ({ ...s, shot: v }))}        decimals={0} />
          <SaisieInput label="Béton"        unit="m³"      value={saisies.m3}          onChange={(v) => setSaisies(s => ({ ...s, m3: v }))}          decimals={0} />
          <SaisieInput label="Durée"        unit="mois"    value={saisies.duree}       onChange={(v) => setSaisies(s => ({ ...s, duree: v }))}       decimals={0} />
          <SaisieInput label="Taux horaire" unit="€/H"     value={saisies.tauxHoraire} onChange={(v) => setSaisies(s => ({ ...s, tauxHoraire: v }))} decimals={2} />
          <div className="ml-auto flex items-center gap-1.5 pt-4">
            <ChartBarIcon className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] text-gray-400">Ratios &amp; PUs calibrés sur · <span className="italic">{template.label}</span></span>
          </div>
        </div>

        {/* Table header */}
        <div className={`${COL_CLASS} pt-4 pb-1.5 border-b border-gray-200`}>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">Libellé</div>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">Unité</div>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider text-right">Ratio</div>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider text-right">Quantités</div>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider text-right">PU</div>
          <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider text-right">Total</div>
        </div>

        {/* Main d'œuvre */}
        <div className="pt-2">
          {moLines.map(renderLine)}
        </div>

        {/* Matériaux */}
        <div className="pt-3">
          <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-[0.08em] pb-1 pl-4">
            Matériaux · Fournitures
          </div>
          {matLines.map(renderLine)}
        </div>

        {/* Sous-total DBS */}
        <div className={`${COL_CLASS} mt-2 py-1.5 border-t border-gray-200 bg-gray-50/70 -mx-2 px-2 rounded`}>
          <span className="col-span-5 text-sm font-semibold text-gray-700">Sous-total DBS</span>
          <span className="text-right text-sm font-bold text-gray-900 tabular-nums">
            {totalDbs.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
          </span>
        </div>

        {/* Frais de chantier */}
        <div className="pt-3">
          <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-[0.08em] pb-1 pl-4">
            Frais de chantier
          </div>
          {fcLines.map(renderLine)}
        </div>

        {/* Sous-total FC */}
        <div className={`${COL_CLASS} mt-2 py-1.5 border-t border-gray-200 bg-gray-50/70 -mx-2 px-2 rounded`}>
          <span className="col-span-5 text-sm font-semibold text-gray-700">Sous-total FC</span>
          <span className="text-right text-sm font-bold text-gray-900 tabular-nums">
            {totalFc.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
          </span>
        </div>

        {/* Grand total */}
        <div className="mt-4 pt-4 border-t-2 border-gray-200">
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em]">
                Sous-total DS + FC
              </span>
              <span className="text-xs text-gray-500">
                DBS {totalDbs.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                {' + '}
                FC {totalFc.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </span>
            </div>
            <div className="text-3xl font-bold text-violet-700 whitespace-nowrap tabular-nums">
              {grandTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="px-1 text-[11px] text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>▸ 4 saisies propagées par ratios historiques</span>
        <span>▸ Ratios &amp; PUs éditables inline (reset d'un clic)</span>
        <span>▸ Clic droit → sélecteur de projets source</span>
        <span>▸ Switch typologie recalibre tout le lot</span>
      </div>

      {/* Source picker modal */}
      {modal && (
        <SourcePickerModal
          title={modal.title}
          subtitle={modal.subtitle}
          unit={modal.unit}
          source={modal.source}
          currentValue={modal.currentValue}
          onApply={modal.onApply}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default RatioGrosOeuvrePrototype;
